import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const drawerWidth = 384;

function SettingsSidebarIcon({ icon: Icon, selected }) {
  if (!Icon) return null;

  return (
    <Box
      component={Icon}
      aria-hidden="true"
      sx={{
        color: selected ? "primary.main" : "text.secondary",
        flexShrink: 0,
        height: 24,
        width: 24,
      }}
    />
  );
}

function SettingsNavigationItem({
  item,
  onItemChange,
  selected,
  showTopBorder,
}) {
  return (
    <Box
      component="a"
      href={item.href}
      onClick={(event) => {
        if (onItemChange) {
          event.preventDefault();
          onItemChange(item.id, item, event);
        }
      }}
      sx={(theme) => ({
        alignItems: "flex-start",
        bgcolor: selected ? theme.palette.action.selected : "transparent",
        borderTop: showTopBorder ? `1px solid ${theme.palette.border.default}` : 0,
        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
        cursor: "pointer",
        display: "flex",
        gap: "12px",
        minHeight: "auto",
        px: "32px",
        py: "20px",
        textDecoration: "none",
        transition: "background-color 150ms ease, color 150ms ease",
        width: "100%",
        "&:hover": {
          bgcolor: selected ? theme.palette.action.selected : theme.palette.action.hover,
          color: selected ? theme.palette.primary.main : theme.palette.text.primary,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.border.focus}`,
          outlineOffset: -2,
        },
      })}
    >
      <SettingsSidebarIcon icon={item.icon} selected={selected} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "inherit",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "24px",
          }}
        >
          {item.label}
        </Typography>
        {item.description ? (
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "21px",
              mt: "2px",
            }}
          >
            {item.description}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function MobileSettingsNavigation({ activeItemId, ariaLabel, items, onItemChange, title }) {
  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.border.default}`,
        display: { xs: "block", md: "none" },
        flexShrink: 0,
      })}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          px: "20px",
          py: "24px",
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: "text.primary",
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: "32px",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Stack
        component="nav"
        aria-label={ariaLabel}
        direction="column"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border.default}`,
          borderTop: `1px solid ${theme.palette.border.default}`,
          overflow: "hidden",
        })}
      >
        {items.map((item, index) => {
          const selected = item.id === activeItemId;

          return (
            <SettingsNavigationItem
              key={item.id}
              item={item}
              selected={selected}
              showTopBorder={index > 0}
              onItemChange={onItemChange}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

export default function SettingsSidebarLayout({
  activeItemId,
  ariaLabel = "Navegacao de configuracoes",
  children,
  contentMaxWidth = 768,
  contentTitle,
  items,
  onItemChange,
  title,
}) {
  return (
    <Box
      sx={{
        bgcolor: "background.default",
        display: { xs: "flex", md: "grid" },
        flexDirection: "column",
        gridTemplateColumns: { md: `${drawerWidth}px minmax(0, 1fr)` },
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        component="aside"
        sx={(theme) => ({
          bgcolor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.border.default}`,
          display: { xs: "none", md: "block" },
          flexShrink: 0,
          height: "100%",
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "auto",
          px: 0,
          py: 0,
        })}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            m: { xs: "24px 20px", md: "40px 24px 40px 32px" },
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: "text.primary",
              fontSize: { xs: 28, md: 32 },
              fontWeight: 800,
              letterSpacing: 0,
              lineHeight: "32px",
            }}
          >
            {title}
          </Typography>
        </Box>

        <Stack
          component="nav"
          aria-label={ariaLabel}
          direction="column"
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.border.default}`,
            borderTop: `1px solid ${theme.palette.border.default}`,
            overflow: "hidden",
          })}
        >
          {items.map((item, index) => {
            const selected = item.id === activeItemId;

            return (
              <SettingsNavigationItem
                key={item.id}
                item={item}
                selected={selected}
                showTopBorder={index > 0}
                onItemChange={onItemChange}
              />
            );
          })}
        </Stack>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "auto",
          px: { xs: 0, md: 4, lg: 6 },
          pb: { xs: 6, md: 6 },
          scrollbarGutter: "stable",
          pt: { xs: 0, md: 4, lg: 6 },
        }}
      >
        <MobileSettingsNavigation
          activeItemId={activeItemId}
          ariaLabel={ariaLabel}
          items={items}
          onItemChange={onItemChange}
          title={title}
        />
        <Box
          sx={{
            maxWidth: contentMaxWidth,
            px: { xs: 3, md: 0 },
            pt: { xs: 4.5, md: 0 },
          }}
        >
          {contentTitle ? (
            <Typography
              component="h2"
              sx={{
                color: "text.primary",
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: "30px",
              }}
            >
              {contentTitle}
            </Typography>
          ) : null}
          <Box sx={{ mt: contentTitle ? 4 : 0 }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
